import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Check, Save, Plus, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, adminHeaders, getToken } from "@/lib/api";
import { formatTimeCs, parseTimeCs } from "@/lib/time";
import { SUIT_SYMBOL, SUIT_COLOR, SUIT_LABEL } from "@/lib/suits";
import { BottomTab } from "@/components/BottomTab";
import { warmupFrom } from "@/lib/warmup";
import { PositionLock } from "@/components/PositionLock";

function FinalRow({ finalist, onSaved }) {
  const [time, setTime] = useState(finalist.time_cs != null ? formatTimeCs(finalist.time_cs) : "");
  const [lane, setLane] = useState(finalist.lane != null ? String(finalist.lane) : "");
  const [busy, setBusy] = useState(false);
  const [posLocked, setPosLocked] = useState(!!finalist.position_locked);
  const [pos, setPos] = useState(finalist.rank ? String(finalist.rank) : "");
  const save = async () => {
    const cs = time.trim() ? parseTimeCs(time) : null;
    if (time.trim() && cs === null) { toast.error("Tempo non valido"); return; }
    const position = posLocked ? parseInt(pos, 10) : null;
    if (posLocked && !(position > 0)) { toast.error("Posizione non valida"); return; }
    setBusy(true);
    try {
      await api.post("/finals/score", { athlete_id: finalist.athlete_id, time_cs: cs, lane: lane.trim() || null, position }, { headers: adminHeaders() });
      toast.success("Tempo salvato");
      onSaved();
    } catch { toast.error("Errore"); } finally { setBusy(false); }
  };
  const removeManual = async () => {
    if (!window.confirm(`Rimuovere ${finalist.name} dai finalisti?`)) return;
    try {
      await api.post("/finals/remove-finalist", { athlete_id: finalist.athlete_id }, { headers: adminHeaders() });
      toast.success("Finalista rimosso");
      onSaved();
    } catch { toast.error("Errore"); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap border-t border-surface3 pt-3" data-testid={`finalrow-${finalist.athlete_id}`}>
      <div className="basis-full sm:basis-0 sm:flex-1 min-w-0">
        <p className="font-black text-onsurface break-words">{finalist.rank ? `${finalist.rank}° ` : ""}{finalist.name}</p>
        <p className="text-[12px] text-onsurface3 flex items-center gap-2 flex-wrap">
          batt. {finalist.batteria_index + 1}
          {finalist.manual && <span className="bg-brand text-onbrand text-[10px] font-black rounded-full px-2 py-0.5">AGGIUNTO</span>}
        </p>
      </div>
      <input className="bg-surface rounded-lg px-2 py-2 font-bold text-onsurface outline-none focus:ring-2 ring-brand text-sm w-14"
        placeholder="lane" inputMode="numeric" value={lane} onChange={(e) => setLane(e.target.value)} data-testid={`finallane-${finalist.athlete_id}`} />
      <input className="bg-surface rounded-lg px-3 py-2 font-bold text-onsurface outline-none focus:ring-2 ring-brand text-sm w-24"
        placeholder="m:ss.cc" value={time} onChange={(e) => setTime(e.target.value)} />
      <PositionLock testid={`final-${finalist.athlete_id}`} locked={posLocked} value={pos}
        onToggle={() => setPosLocked(!posLocked)} onChange={setPos} />
      <button onClick={save} disabled={busy} className="bg-black text-brand rounded-lg p-2" data-testid={`finalsave-${finalist.athlete_id}`}><Save size={18} /></button>
      {finalist.manual && (
        <button onClick={removeManual} className="bg-mberror/15 text-mberror rounded-lg p-2" data-testid={`finalremove-${finalist.athlete_id}`}><X size={18} /></button>
      )}
    </div>
  );
}

function AddFinalist({ suit, candidates, onAdded }) {
  const [sel, setSel] = useState("");
  if (!candidates || candidates.length === 0) return null;
  const add = async () => {
    if (!sel) { toast.error("Seleziona un atleta"); return; }
    try {
      await api.post("/finals/add-finalist", { athlete_id: sel }, { headers: adminHeaders() });
      toast.success("Finalista aggiunto");
      setSel("");
      onAdded();
    } catch { toast.error("Errore"); }
  };
  return (
    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-surface3">
      <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid={`addfinalist-select-${suit}`}
        className="flex-1 min-w-0 bg-surface rounded-lg px-2 py-2 font-bold text-onsurface outline-none focus:ring-2 ring-brand text-sm">
        <option value="">+ Aggiungi finalista…</option>
        {candidates.map((c) => (
          <option key={c.athlete_id} value={c.athlete_id}>{c.name} (batt. {c.batteria_index + 1})</option>
        ))}
      </select>
      <button onClick={add} className="bg-brand text-onbrand rounded-lg px-3 py-2 font-black flex items-center gap-1" data-testid={`addfinalist-btn-${suit}`}>
        <Plus size={16} /> Aggiungi
      </button>
    </div>
  );
}

function FinalsSchedule({ gender, suit, s }) {
  const [r, setR] = useState(s.race_time || "");
  const save = async () => {
    try {
      await api.post("/finals/schedule", { gender, suit, race_time: r }, { headers: adminHeaders() });
      toast.success("Orari salvati");
    } catch { toast.error("Errore"); }
  };
  const input = "bg-surface rounded-lg px-2 py-1.5 font-bold text-onsurface outline-none focus:ring-2 ring-brand text-sm w-24";
  return (
    <div className="flex items-center gap-2 flex-wrap mb-2">
      <span className="text-xs text-onsurface3 font-bold">Gara</span>
      <input className={input} placeholder="HH:MM" value={r} onChange={(e) => setR(e.target.value)} data-testid={`finals-race-${suit}`} />
      <button onClick={save} className="bg-black text-brand rounded-lg p-2" data-testid={`finals-sched-save-${suit}`}><Check size={16} /></button>
      <span className="text-xs text-onsurface3 font-bold" data-testid={`finals-warmup-${suit}`}>Warm up {warmupFrom(r) || "—"} (auto)</span>
    </div>
  );
}

export default function AdminFinals() {
  const nav = useNavigate();
  const [gender, setGender] = useState("Men");
  const [data, setData] = useState(null);
  const [candidates, setCandidates] = useState({});

  const load = useCallback(() => {
    api.get(`/finals?gender=${gender}`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/finals/candidates?gender=${gender}`).then((r) => {
      const map = {};
      (r.data.suits || []).forEach((s) => { map[s.suit] = s.athletes; });
      setCandidates(map);
    }).catch(() => {});
  }, [gender]);
  useEffect(() => { if (!getToken()) { nav("/admin"); return; } load(); }, [load, nav]);

  return (
    <div className="min-h-screen bg-surface pb-24" data-testid="admin-finals-page">
      <Toaster position="top-center" richColors />
      <header className="bg-black px-4 pb-4" style={{ paddingTop: "calc(var(--safe-top) + 16px)" }}>
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/admin")} className="text-white"><ChevronLeft size={32} strokeWidth={2.5} /></button>
          <h1 className="text-2xl font-black text-white">Gestisci Finals</h1>
        </div>
        <div className="mt-4 flex gap-2 bg-[#1c1c1e] p-1 rounded-full">
          {[["Men", "Uomo"], ["Women", "Donna"]].map(([v, l]) => (
            <button key={v} data-testid={`finals-gender-${v.toLowerCase()}`} onClick={() => setGender(v)}
              className={`flex-1 py-2.5 rounded-full font-bold ${gender === v ? "bg-brand text-onbrand" : "text-white"}`}>{l}</button>
          ))}
        </div>
      </header>

      <div className="px-4 pt-4">
        {!data ? null : data.suits.length === 0 ? (
          <p className="text-onsurface3 text-center py-16">Nessun finalista. I finalisti vengono generati dai Games.</p>
        ) : (
          data.suits.map((s) => (
            <div key={s.suit} className="bg-surface2 rounded-2xl p-4 mb-4">
              <p className="font-black text-lg flex items-center gap-2 mb-3">
                <span style={{ color: SUIT_COLOR[s.suit] }}>{SUIT_SYMBOL[s.suit]}</span>FINALE {SUIT_LABEL[s.suit]}
              </p>
              <FinalsSchedule gender={gender} suit={s.suit} s={s} />
              <div className="space-y-3">
                {s.finalists.map((f) => <FinalRow key={`${f.athlete_id}-${f.rank}`} finalist={f} onSaved={load} />)}
              </div>
              <AddFinalist suit={s.suit} candidates={candidates[s.suit]} onAdded={load} />
            </div>
          ))
        )}
      </div>
      <BottomTab />
    </div>
  );
}
